import React, {useEffect, useState} from "react";
import SyntaxHighlighter from 'react-syntax-highlighter';
import xcode from 'react-syntax-highlighter/dist/cjs/styles/hljs/xcode';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    makeStyles,
    MenuItem,
    TextField,
    Typography
} from "@material-ui/core";
import Strings from "../lang/Strings";
import BoxSelect from "./BoxSelect";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MaterialSnackBar from "./MaterialSnackBar";
import {generateLuckyNumber, generateWinningNumbers, SHA512Digest} from '../utils/VerificationAlgorithm';
import {green, red} from "@material-ui/core/colors";
import AppColors from "../colors/AppColors";
import PageUtils from "../utils/PageUtils";

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    heading: {
        fontSize: theme.typography.pxToRem(15),
        fontWeight: theme.typography.fontWeightRegular,
    },
}));

export default function VerifyGames() {

    const classes = useStyles();
    const [gameType, setGameType] = useState(-1);
    const [serverSeed, setServerSeed] = useState("");
    const [serverSeedHash, setServerSeedHash] = useState("");
    const [clientSeed, setClientSeed] = useState("");
    const [nonce, setNonce] = useState("");

    const [openSnack, setOpenSnack] = useState(false);
    const [snackMessage, setSnackMessage] = useState("");
    const [snackSeverity, setSnackSeverity] = useState("warning");

    const [serverSeedMatches, setServerSeedMatches] = useState(false);
    const [result, setResult] = useState("");
    const [resultHeader, setResultHeader] = useState("");

    useEffect(() => {
        let refObject = PageUtils.getPageParams(window.location.href.toString());
        setServerSeed(refObject["server_seed"]);
        setServerSeedHash(refObject["server_seed_hash"]);
        setClientSeed(refObject["client_seed"]);
        setNonce(refObject["nonce"]);
        let capturedGameType = refObject["gametype"];
        if (capturedGameType) {
            setGameType(parseInt(capturedGameType));
        }
    }, [window.location.href]);

    const handleSnackClose = () => {
        setOpenSnack(false);
    }

    const handleServerSeedChange = (value: string) => {
        setServerSeed(value);
    }

    const handleServerSeedHashChanged = (value: string) => {
        setServerSeedHash(value);
    }

    const handleClientSeedChanged = (value: string) => {
        setClientSeed(value);
    }

    const handleNonceChanged = (value: string) => {
        setNonce(value);
    }

    const handleGameTypeChange = (event: any) => {
        let gameSelection = event.target.value;
        if (gameSelection != -1) {
            setGameType(gameSelection);
        }
    }

    const ResultView = () => {
        return <div>
            <Typography
                align={"center"}
                style={{
                    color: serverSeedMatches ? green[800] : red[800],
                    fontWeight: 600,
                    marginTop: 20,
                    fontSize: 20,
                    marginBottom: 10,
                }}>Server Seeds Match ? {(serverSeedMatches + '').toUpperCase()}
            </Typography>
            <Typography
                align={"center"}
                style={{
                    fontSize: 16,
                    fontWeight: 400,
                    paddingBottom: 10,
                }}>{resultHeader}</Typography>
            <Typography align={"center"} style={{
                fontSize: 18,
                fontWeight: 600,
                paddingBottom: 10,
            }}>{result}</Typography>
        </div>
    }

    const snackError = (message: string) => {
        setSnackMessage(message);
        setSnackSeverity("error");
        setOpenSnack(true);
    }

    const verifyGames = () => {
        if (gameType == -1) {
            snackError("Please select a game type");
            return;
        }
        if (serverSeed == "") {
            snackError("Please enter the server seed");
            return;
        }
        if (serverSeedHash == "") {
            snackError("Please enter the server seed hash");
            return;
        }
        if (clientSeed == "") {
            snackError("Please enter the Client Seed");
            return;
        }
        if (nonce == "") {
            snackError("Please enter the nonce");
            return;
        }
        let serverSeedTest = SHA512Digest(serverSeed);
        setServerSeedMatches(serverSeedTest.toLowerCase() === serverSeedHash.toLowerCase());
        if (gameType === 0) {
            let roll = generateLuckyNumber(serverSeed, clientSeed, nonce);
            setResultHeader("ROLL IS:");
            setResult(roll + '');
        } else {
            let winningNumbers = generateWinningNumbers(serverSeed, clientSeed, nonce);
            setResultHeader("WINNING NUMBERS ARE:");
            setResult(winningNumbers.toString());
        }
    }

    return <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    }}>
        <Typography
            align={"center"}
            style={{
                fontSize: 45,
                paddingTop: 10,
                paddingBottom: 10
            }}
            dangerouslySetInnerHTML={{__html: `<b style="font-weight: 600">${Strings.APP_NAME}</b> Rolls/Winning Numbers Verifier`}}/>
        <Typography
            align={"center"}
            style={{
                fontSize: 20,
                paddingTop: 10,
                paddingBottom: 10,
                paddingLeft: 30,
                paddingRight: 30
            }}
            dangerouslySetInnerHTML={{
                __html: `
        Enter the details from the section titled <b>PREVIOUS GAME/ROLL DETAILS</b> that you can see after you click on the link that says <b>PROVABLY FAIR</b> to verify that the Winning Numbers or Roll you rolled were indeed provably fair (ie. we did not change the Roll or Winning Numbers outcome after you clicked on ROLL or PLAY and that we did indeed use the same server and client seeds that we showed you before the roll or draw, to generate the rolled number or Winning Numbers).

This roll/winning numbers verifier is entirely coded in javascript so you can view the source to see how the roll is calculated and we will not be able to manipulate this script in any way because the calculations are done right here in your browser and the source is open for everyone to see.

        `
            }}/>

        <div style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginTop: 20,
        }}>
            <TextField
                value={serverSeed}
                onChange={(event) => handleServerSeedChange(event.target.value + '')}
                label="SERVER SEED"
                style={{margin: 8}}
                variant={"outlined"}
                margin="normal"
                InputLabelProps={{
                    shrink: true,
                }}
            />
            <TextField
                label="SERVER SEED HASH"
                value={serverSeedHash}
                onChange={(event) => handleServerSeedHashChanged(event.target.value + '')}
                style={{margin: 8}}
                variant={"outlined"}
                margin="normal"
                InputLabelProps={{
                    shrink: true,
                }}
            />
        </div>
        <div style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginTop: 20,
        }}>
            <TextField
                value={clientSeed}
                onChange={(event) => handleClientSeedChanged(event.target.value + '')}
                label="CLIENT SEED"
                style={{margin: 8}}
                variant={"outlined"}
                margin="normal"
                InputLabelProps={{
                    shrink: true,
                }}
            />
            <TextField
                value={nonce}
                onChange={(event) => handleNonceChanged(event.target.value + '')}
                label="NONCE"
                style={{margin: 8}}
                variant={"outlined"}
                margin="normal"
                InputLabelProps={{
                    shrink: true,
                }}
            />
        </div>
        <BoxSelect
            labelId="game-selector-label"
            id="game-selector"
            value={gameType}
            style={{
                marginTop: 10
            }}
            onChange={handleGameTypeChange}>
            <MenuItem value={-1} disabled>
                SELECT GAME TYPE
            </MenuItem>
            <MenuItem value={0}>
                FREE BTC
            </MenuItem>
            <MenuItem value={1}>
                6/49 GAME
            </MenuItem>
        </BoxSelect>

        <Button
            onClick={verifyGames}
            variant={"contained"}
            style={{
                marginTop: 20,
                width: 150,
            }}>VERIFY
        </Button>
        {
            result !== "" &&
            <ResultView/>
        }
        <Accordion style={{
            marginTop: 20,
            marginLeft: 20,
            marginRight: 20,
        }}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon/>}
                aria-controls="panel1a-content"
                id="panel1a-header"
                style={{
                    backgroundColor: AppColors.EASE_GRAY
                }}>
                <Typography className={classes.heading}>View The Source Code</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <SyntaxHighlighter language="javascript" style={xcode}>
                    {Strings.SOURCE_CODE_STRING}
                </SyntaxHighlighter>
            </AccordionDetails>
        </Accordion>
        <MaterialSnackBar open={openSnack} onSnackClosed={handleSnackClose} message={snackMessage}
                          severity={snackSeverity}/>
    </div>
}